import type { ExcelField, LLMInstance, OCRInstance, ImageEditInstance, ImageEditProvider, ImageEditSettings, TitleOptimizationConfig, LowQualityFilterConfig, SizeChartConfig, OCRSettings, FlagColor, SkuConfig } from '@/types';

// Excel 字段定义 (22列，按示例文件顺序)
export const EXCEL_FIELDS: ExcelField[] = [
    { key: 'productId', label: '*产品主编号', index: 1, isImage: false, required: true },
    { key: 'productName', label: '*产品名称', index: 2, isImage: false, required: true },
    { key: 'currency', label: '货币类型', index: 3, isImage: false },
    { key: 'mainImages', label: '产品主图', index: 4, isImage: true },
    { key: 'sourceUrl', label: '货源链接', index: 5, isImage: false },
    { key: 'sourcePlatform', label: '货源平台', index: 6, isImage: false },
    { key: 'sourceId', label: '货源ID', index: 7, isImage: false },
    { key: 'description', label: '详情描述', index: 8, isImage: false },
    { key: 'detailImages', label: '详情图', index: 9, isImage: true },
    { key: 'sourceCategory', label: '货源类目', index: 10, isImage: false },
    { key: 'customAttrs', label: '自定义属性', index: 11, isImage: false },
    { key: 'productVideo', label: '产品视频', index: 12, isImage: false },
    { key: 'productCert', label: '产品证书', index: 13, isImage: false },
    { key: 'sizeChart', label: '尺寸图表', index: 14, isImage: true },
    { key: 'skuSpec1', label: 'SKU规格1', index: 15, isImage: false },
    { key: 'skuSpec2', label: 'SKU规格2', index: 16, isImage: false },
    { key: 'platformSku', label: '平台SKU', index: 17, isImage: false },
    { key: 'skuPrice', label: '*SKU售价', index: 18, isImage: false, required: true },
    { key: 'skuImage', label: 'SKU图片', index: 19, isImage: true },
    { key: 'skuStock', label: 'SKU库存', index: 20, isImage: false },
    { key: 'skuWeight', label: 'SKU重量(KG)', index: 21, isImage: false },
    { key: 'skuSize', label: 'SKU尺寸(CM)', index: 22, isImage: false },
];

// 首图字段 (虚拟字段，派生自产品主图第一个链接，不入库 Excel)
export const FIRST_IMAGE_FIELD: ExcelField = {
    key: 'firstImage',
    label: '首图',
    index: 0, // 虚拟字段，不入 Excel
    isImage: true,
    isVirtual: true,
};

// 前端表格显示顺序：优先字段在前，其余按原顺序追加
export const DISPLAY_PRIORITY_FIELDS = [
    'productId',    // 产品主编号
    'productName',  // 标题
    'firstImage',   // 首图
    'skuSpec1',     // SKU规格1
    'skuSpec2',     // SKU规格2
    'platformSku',  // 平台SKU
    'skuWeight',    // SKU重量
    'skuPrice',     // SKU售价
    'skuImage',     // SKU图片
];

// SKU 级别字段：同一产品的不同 SKU 这些字段可能不同
export const SKU_SPECIFIC_FIELDS = new Set([
    'skuSpec1', 'skuSpec2', 'platformSku', 'skuPrice',
    'skuImage', 'skuStock', 'skuWeight', 'skuSize',
]);

// 图片字段 key 集合
export const IMAGE_FIELD_KEYS = EXCEL_FIELDS.filter(f => f.isImage).map(f => f.key);

// 字段 key -> 中文标签 映射，便于在提示与日志中展示
export const EXCEL_FIELD_LABELS: Record<string, string> = Object.fromEntries(
    EXCEL_FIELDS.map(f => [f.key, f.label]),
);

// 默认说明行文本
export const DEFAULT_DESCRIPTION = `各个字段说明：
1、产品主编号：一个SKU一行,若多个SKU同属于一个产品,请填入相同的产品主编号
2、产品主图：输入图片链接，多个用中文逗号分割。注意：同一产品只需第一个SKU填写产品主图，其他SKU可忽略此列；
3、详情图：输入图片链接`;

// 图片链接分隔符
export const IMAGE_SEPARATOR = '，';

// 默认 LLM 实例
export const DEFAULT_LLM_INSTANCES: LLMInstance[] = [
    {
        id: 'default-deepseek',
        name: 'DeepSeek',
        apiUrl: 'https://api.deepseek.com',
        apiKey: '',
        models: ['deepseek-v4-flash', 'deepseek-v4-pro'],
    },
    {
        id: 'default-qwen',
        name: '通义千问',
        apiUrl:
            'https://ws-wfw5j13yn8rm4c89.cn-beijing.maas.aliyuncs.com/compatible-mode/v1',
        apiKey: '',
        models: ['qwen3.7-max', 'qwen3.7-plus'],
    },
];

// 默认 OCR 实例
export const DEFAULT_OCR_INSTANCES: OCRInstance[] = [
    {
        id: 'default-local-tesseract',
        name: '本地 OCR（中英文）',
        type: 'LocalTesseract',
        apiUrl: 'local://tesseract',
        token: '',
        model: 'Tesseract.js chi_sim+eng',
        enabled: true,
        quota: 0,
        usedCount: 0,
    },
    {
        id: 'default-paddleocr',
        name: 'PaddleOCR',
        type: 'PaddleOCR',
        apiUrl: 'https://paddleocr.aistudio-app.com/api/v2/ocr/jobs',
        token: '',
        model: 'PaddleOCR-VL-1.6',
        enabled: false,
        quota: 1000,
        usedCount: 0,
    },
];

// 默认 AI 图片编辑实例
export const IMAGE_EDIT_PROVIDERS: { value: ImageEditProvider; label: string; desc: string }[] = [
    { value: 'relay', label: 'Relay（异步 Job）', desc: '自建 relay 图片服务' },
    { value: 'nano-banana', label: 'NanoBanana（gr-sai）', desc: 'gr-sai NanoBanana 同步接口' },
];

export const NANO_BANANA_MODELS = [
    'nano-banana', 'nano-banana-fast', 'nano-banana-2', 'nano-banana-2-cl',
    'nano-banana-2-2k-cl', 'nano-banana-2-4k-cl', 'nano-banana-pro',
    'nano-banana-pro-vt', 'nano-banana-pro-cl', 'nano-banana-pro-vip', 'nano-banana-pro-4k-vip',
];

export const DEFAULT_IMAGE_EDIT_INSTANCES: ImageEditInstance[] = [
    {
        id: 'default-image-edit',
        name: 'Luchi 图生图',
        apiUrl: 'https://image.luchikey.com',
        apiKey: '',
        model: 'gpt-image-2',
        apiPath: '/api/relay/image-jobs/edits',
        defaultRatio: '1:1',
        defaultQuality: 'standard',
        defaultCount: 1,
    },
    {
        id: 'default-nano-banana',
        name: 'NanoBanana 图生图',
        provider: 'nano-banana',
        apiUrl: 'https://grsaiapi.com',
        apiKey: '',
        model: 'nano-banana-2',
        apiPath: '/v1/api/generate',
        defaultRatio: '1:1',
        defaultQuality: 'standard',
        defaultCount: 1,
    },
];

// 默认 AI 图片编辑全局设置
export const DEFAULT_IMAGE_EDIT_SETTINGS: ImageEditSettings = {
    jdcloudStorageEnabled: true,
    browserCompressionEnabled: true,
    compressionLevel: 'medium',
    defaultRatio: undefined,
    defaultQuality: undefined,
    defaultCount: undefined,
    presetPrompts: ['主体保持不变，文字保持不变，背景替换为更适合产品使用的场景'],
};

// 默认标题优化配置
export const DEFAULT_TITLE_CONFIG: TitleOptimizationConfig = {
    enabled: false,
    llmInstanceId: '',
    model: '',
    requestInterval: 10,
    submissionMode: 'json',
    batchProductCount: 10,
    prompt: `1. 保留核心产品词、材质、规格、功能、款式、适用场景等关键信息，不篡改产品属性；2. 若商品带有品牌名，品牌词放置在标题最前端；3. 合理嵌入高热度搜索关键词，提升搜索曝光；4. 语句通顺自然，符合海外买家阅读逻辑，无语法错误，中文标题；5. 控制标题字符长度适配主流跨境平台规范，剔除冗余、重复词汇；6. 将标题拆分成多个关键词，所有关键词统一用空格分隔；7. 只返回优化后的标题即可，不用返回其他信息；8. 有些商品名称中重复使用了同一个关键词超过 3 次（即所谓的"关键词滥用"）。例如："防晒外套"、"长袖外套"、"女士长袖外套"、"开衫外套"等。请将这些描述修改为更合理的表达方式，比如"长袖防晒外套"、"女士开衫外套"等。另外，如果句子的表述不恰当，也需要进行修改。例如："深受喜爱的韩国风格男女冬夏服装"，应修改为"韩国风格的女士冬装"。这种属于劣质的标题，要避开这种标题反向优化。你是一个电商商品标题优化专家。请根据以下规则优化商品标题：
1. 保留商品核心信息（品牌、品类、型号等关键信息不可丢失）
2. 品牌名称前置，放在标题最前面
3. 嵌入相关搜索关键词，提升搜索曝光
4. 语句通顺，符合中文语法和阅读习惯
5. 标题长度控制在 30-60 个字符之间
6. 关键词之间用空格分隔
7. 仅返回优化后的标题文本，不要返回任何解释或额外内容
8. 避免关键词堆砌和滥用，保持自然可读`,
};

// 默认低质量图片关键词
export const DEFAULT_LOW_QUALITY_KEYWORDS = [
    '收藏', '加购', '关注', '领券', '优惠券', '满减', '限时', '抢购',
    '立即购买', '马上抢', '点击领取', '免费领取', '淘宝',
];

// 默认低质量过滤配置
export const DEFAULT_LOW_QUALITY_CONFIG: LowQualityFilterConfig = {
    enabled: false,
    keywords: [...DEFAULT_LOW_QUALITY_KEYWORDS],
    filterDetailImages: true,
    filterMainImages: false,
    filterSkuImages: false,
    removeSkuRowIfEmpty: true,
};

// 默认尺码关键词
export const DEFAULT_SIZE_KEYWORDS = [
    '尺码', '衣长', '袖长', '臀围', '胸围', '肩宽', '裤长',
];

// 默认尺码图识别配置
export const DEFAULT_SIZE_CHART_CONFIG: SizeChartConfig = {
    enabled: false,
    keywords: [...DEFAULT_SIZE_KEYWORDS],
};

// 默认 OCR 设置
export const DEFAULT_OCR_SETTINGS: OCRSettings = {
    requestInterval: 10,
};

// 产品旗标颜色配置 (默认灰色)
export const FLAG_OPTIONS: { color: FlagColor; label: string; textClass: string }[] = [
    { color: 'gray', label: '灰色', textClass: 'text-zinc-400' },
    { color: 'red', label: '红色', textClass: 'text-red-500' },
    { color: 'yellow', label: '黄色', textClass: 'text-yellow-500' },
    { color: 'blue', label: '蓝色', textClass: 'text-blue-500' },
    { color: 'green', label: '绿色', textClass: 'text-green-500' },
    { color: 'purple', label: '紫色', textClass: 'text-purple-500' },
    { color: 'orange', label: '橙色', textClass: 'text-orange-500' },
];

export const DEFAULT_FLAG: FlagColor = 'gray';

// 默认 SKU 配置
export const LEGACY_DEFAULT_SKU_SPEC_PROMPT = '提取 sku 重点，去掉无用字符，无用表情符号等，如果超过 30 个字符，将 sku 浓缩在 30 个字符以内';

export const DEFAULT_SKU_CONFIG: SkuConfig = {
    // sku 规格优化
    specEnabled: false,
    specLlmInstanceId: '',
    specModel: '',
    specPrompt: `${LEGACY_DEFAULT_SKU_SPEC_PROMPT}。同一个商品内，规格值得各自唯一`,
    specFields: ['skuSpec1', 'skuSpec2'],
    specRequestInterval: 10,
    specSkipShortEnabled: false,
    specSkipShortChars: 30,
    specSubmissionMode: 'batch-json',
    specBatchProductCount: 10,
    // sku 估重
    weightEnabled: false,
    weightLlmInstanceId: '',
    weightModel: '',
    weightPrompt: '根据已知产品内容，估算本 sku 的重量，单位 Kg，精度 0.01',
    weightRequestInterval: 10,
    weightUseImage: false,
    weightMultiRound: false,
    weightRepeatCount: 1,
    weightSubmissionMode: 'batch-json',
    weightBatchProductCount: 10,
    overwritePlatformSpec: false,
};

// localStorage 存储键
export const STORAGE_KEYS = {
    LLM_INSTANCES: 'tabbygrid_llm_instances',
    OCR_INSTANCES: 'tabbygrid_ocr_instances',
    OCR_SETTINGS: 'tabbygrid_ocr_settings',
    IMAGE_EDIT_INSTANCES: 'tabbygrid_image_edit_instances',
    IMAGE_EDIT_SETTINGS: 'tabbygrid_image_edit_settings',
    TITLE_CONFIG: 'tabbygrid_title_config',
    LOW_QUALITY_CONFIG: 'tabbygrid_low_quality_config',
    SIZE_CHART_CONFIG: 'tabbygrid_size_chart_config',
    SKU_CONFIG: 'tabbygrid_sku_config',
    OCR_USAGE: 'tabbygrid_ocr_usage',
    VISIBLE_FIELDS: 'tabbygrid_visible_fields',
    FIELD_ORDER: 'tabbygrid_field_order',
    STEP_CONFIRM_ENABLED: 'tabbygrid_step_confirm_enabled',
    STEP_BATCH_SIZE: 'tabbygrid_step_batch_size',
};

// LLM 调用参数
export const LLM_PARAMS = {
    TEMPERATURE: 0.3,
    MAX_TOKENS: 500,
};

// OCR 配额预警阈值 (90%)
export const OCR_QUOTA_THRESHOLD = 0.9;
